import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, Type, inject } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

import {
  FormFieldConfig,
} from './models/form-wizard.models';
import {
  FormWizardCustomFieldAdapter,
  FormWizardFieldRegistryService,
} from './form-wizard-field-registry.service';

@Component({
  selector: 'app-form-wizard-field-renderer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    TooltipModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-wizard-field-renderer.component.html',
  styleUrls: ['./form-wizard-field-renderer.component.scss'],
})
export class FormWizardFieldRendererComponent<T = any> {
  private readonly fieldRegistry = inject(FormWizardFieldRegistryService);

  @Input({ required: true }) field!: FormFieldConfig<T>;
  @Input({ required: true }) form!: FormGroup;
  @Input() layout: 'default' | 'modal' = 'default';

  get customFieldComponent(): Type<FormWizardCustomFieldAdapter> | null {
    return this.fieldRegistry.resolve(this.field.type);
  }

  get customFieldInputs(): Record<string, unknown> {
    return {
      field: this.field,
      form: this.form,
    };
  }

  getFieldValue(fieldKey: string): any {
    return this.form.get(fieldKey)?.value;
  }

  isFieldInvalid(fieldKey: string): boolean {
    const control = this.form.get(fieldKey);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getFieldError(fieldKey: string): string {
    const control = this.form.get(fieldKey);
    if (!control?.errors) {
      return '';
    }

    const errors = control.errors;

    if (this.field.validations) {
      for (const validation of this.field.validations) {
        if (errors[validation.type] && validation.message) {
          return validation.message;
        }
      }
    }

    if (errors['required']) return 'To pole jest wymagane';
    if (errors['email']) return 'Nieprawidłowy adres email';
    if (errors['pattern']) return 'Nieprawidłowy format';
    if (errors['min']) return `Minimalna wartość to ${errors['min'].min}`;
    if (errors['max']) return `Maksymalna wartość to ${errors['max'].max}`;
    if (errors['minlength']) {
      return `Minimalna długość to ${errors['minlength'].requiredLength} znaków`;
    }
    if (errors['maxlength']) {
      return `Maksymalna długość to ${errors['maxlength'].requiredLength} znaków`;
    }

    return 'Nieprawidłowa wartość';
  }

  getSelectOptions(): any[] {
    if (this.field.dynamicOptions) {
      return this.field.dynamicOptions(this.form.getRawValue());
    }

    if (!this.field.options || typeof (this.field.options as any).subscribe === 'function') {
      return [];
    }

    return this.field.options as any[];
  }

  triggerOnBlur(): void {
    if (this.field.onBlur) {
      this.field.onBlur(this.form.get(this.field.key)?.value, this.form);
    }
  }

  handleNumberKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const key = event.key;

    if (key !== '.' && key !== ',') {
      return;
    }

    const locale = this.field.type === 'currency' ? 'pl-PL' : 'en-US';
    const expectedSeparator = locale === 'pl-PL' ? ',' : '.';
    const otherSeparator = locale === 'pl-PL' ? '.' : ',';

    if (key !== otherSeparator) {
      return;
    }

    event.preventDefault();

    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const value = input.value;

    if (value.includes(expectedSeparator)) {
      return;
    }

    const newValue = value.substring(0, start) + expectedSeparator + value.substring(end);
    input.value = newValue;
    input.setSelectionRange(start + 1, start + 1);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');

    if (value.length > 8) {
      value = value.substring(0, 8);
    }

    let formatted = value;
    if (value.length > 4) {
      formatted = value.substring(0, 4) + '-' + value.substring(4);
    }
    if (value.length > 6) {
      formatted = formatted.substring(0, 7) + '-' + formatted.substring(7);
    }

    input.value = formatted;
    const control = this.form.get(this.field.key);
    if (control) {
      control.setValue(formatted as never, { emitEvent: true });
    }
  }

  getNumberValue(value: unknown): number | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === 'number') {
      return value;
    }

    const num = parseFloat(String(value));
    return Number.isNaN(num) ? undefined : num;
  }
}
